import {Camera} from "@react-three/fiber";
import * as Three from "three";
import {create, StoreApi, UseBoundStore} from "zustand";

import {initialPartSearchRefinements, PartSearchRefinements} from "./models/PartSearchRefinements";

type LibrarySearchState = {
    query: string | undefined;
    refinements: PartSearchRefinements;
};

type DocumentUiState = {
    remoteMouseRef?: React.RefObject<Three.Mesh>;
    pcbCamera?: Camera;
    embedMode: boolean;
    isVersionHistoryMode: boolean;
    librarySearch: LibrarySearchState;
    pcbObjectsFilter: string;
    schematicObjectsFilter: string;
    rulesFilter: string;
    hideIfNotStarredByCurrentUser: boolean;
    showAllPinLabels: boolean;
    showCollaborativeCursors: boolean;
    newlyCreatedRulesetUid: string | undefined;
    rulesetUidToNavigateTo: string | undefined;
    algoliaRefinementsClearFn?: () => void;
    showResultsBar: boolean;

    // Comment-related stuff
    selectedCommentThreadUid: string | undefined;
    newCommentMessage: string | undefined;
    showComments: boolean;
    projectChatPrefillMessage: string | undefined;
    activeProjectDocId: string | undefined;
};

type DocumentUiApi = {
    // Note: this is only used once, for camera zoom - rethink the api?
    setPcbCamera: (camera: Camera) => void;

    setEmbedMode: (embedMode: boolean) => void;

    showChangeHistoryPanel: () => void;
    hideChangeHistoryPanel: () => void;

    documentUidChanged: () => void;

    setComponentBrowserSearch: (searchQuery: string | undefined) => void;
    updateComponentSearchRefinements: (update: Partial<PartSearchRefinements>) => void;
    clearComponentSearch: () => void;
    clearComponentSearchRefinements: () => void;

    setAlgoliaRefinementsClearFn: (clearFn: () => void) => void;
    clearAlgoliaRefinements: () => void;

    setShowResultsBar: (show: boolean) => void;

    setPcbObjectsFilter: (filter: string) => void;
    setSchematicObjectsFilter: (filter: string) => void;
    setRulesFilter: (filter: string) => void;

    setHideIfNotStarredByCurrentUser: (value: boolean) => void;
    setShowAllPinLabels: (show: boolean) => void;
    setShowComments: (show: boolean) => void;
    setShowCollaborativeCursors: (show: boolean) => void;

    // Ruleset-related stuff
    setNewlyCreatedRulesetUid: (rulesetUid: string) => void;
    clearNewlyCreatedRulesetUid: () => void;
    setRulesetUidToNavigateTo: (rulesetUid: string) => void;
    clearRulesetUidToNavigateTo: () => void;

    // Comment-related stuff
    hideCommentThreads: () => void;
    showCommentThread: (commentThreadUid: string, prefillMessage?: string) => void;
    toggleCommentThread: (commentThreadUid: string) => void;
    prefillProjectChatMessage: (text: string) => void;
    removeProjectChatPrefill: () => void;

    setActiveProjectDocId: (docId: string | undefined) => void;
};

type DocumentUiStoreState = DocumentUiState & DocumentUiApi;

export type UseDocumentUiStore = UseBoundStore<StoreApi<DocumentUiStoreState>>;

export const createUseDocumentUiStoreHook = () =>
    create<DocumentUiStoreState>((set, get) => ({
        setPcbCamera: (camera) =>
            set((state) => ({
                ...state,
                pcbCamera: camera,
            })),
        embedMode: false,
        setEmbedMode: (embedMode) => {
            set((state) => {
                return {...state, embedMode: embedMode};
            });
        },
        newlyCreatedRulesetUid: undefined,
        setNewlyCreatedRulesetUid: (rulesetUid: string) => {
            set((state) => ({
                ...state,
                newlyCreatedRulesetUid: rulesetUid,
            }));
        },
        clearNewlyCreatedRulesetUid: () => {
            set((state) => ({
                ...state,
                newlyCreatedRulesetUid: undefined,
            }));
        },
        rulesetUidToNavigateTo: undefined,
        setRulesetUidToNavigateTo: (rulesetUid: string) => {
            set((state) => ({
                ...state,
                rulesetUidToNavigateTo: rulesetUid,
            }));
        },
        clearRulesetUidToNavigateTo: () => {
            set((state) => ({
                ...state,
                rulesetUidToNavigateTo: undefined,
            }));
        },
        librarySearch: {
            query: undefined,
            refinements: initialPartSearchRefinements,
        },
        showResultsBar: false,
        setShowResultsBar: (show: boolean) => {
            set((state) => ({
                ...state,
                showResultsBar: show,
            }));
        },
        setComponentBrowserSearch: (searchQuery) => {
            set((state) => {
                const newLibrarySearch = {refinements: state.librarySearch.refinements, query: searchQuery};
                return {...state, librarySearch: newLibrarySearch, showResultsBar: Boolean(searchQuery)};
            });
        },
        updateComponentSearchRefinements: (update) => {
            set((state) => {
                const newRefinements = {...state.librarySearch.refinements, ...update};
                const newLibrarySearch = {query: state.librarySearch.query, refinements: newRefinements};
                return {...state, librarySearch: newLibrarySearch, showResultsBar: true};
            });
        },
        setAlgoliaRefinementsClearFn: (clearFn: () => void) => {
            set((state) => ({
                ...state,
                algoliaRefinementsClearFn: clearFn,
            }));
        },
        clearAlgoliaRefinements: () => {
            const state = get();
            if (state.algoliaRefinementsClearFn) {
                state.algoliaRefinementsClearFn();
            }
        },
        clearComponentSearchRefinements: () => {
            set((state) => {
                const newLibrarySearch = {query: state.librarySearch.query, refinements: initialPartSearchRefinements};
                return {...state, librarySearch: newLibrarySearch, showResultsBar: Boolean(state.librarySearch.query)};
            });
            get().clearAlgoliaRefinements();
        },
        clearComponentSearch: () => {
            set((state) => {
                const newLibrarySearch = {query: "", refinements: initialPartSearchRefinements};
                return {...state, librarySearch: newLibrarySearch, showResultsBar: false};
            });
            get().clearAlgoliaRefinements();
        },
        pcbObjectsFilter: "",
        setPcbObjectsFilter: (filter) => {
            set((state) => ({
                ...state,
                pcbObjectsFilter: filter,
            }));
        },
        schematicObjectsFilter: "",
        setSchematicObjectsFilter: (filter) => {
            set((state) => ({
                ...state,
                schematicObjectsFilter: filter,
            }));
        },
        rulesFilter: "",
        setRulesFilter: (filter) => {
            set((state) => ({
                ...state,
                rulesFilter: filter,
            }));
        },
        isVersionHistoryMode: false,
        showChangeHistoryPanel: () => {
            set((state) => {
                return {...state, isVersionHistoryMode: true};
            });
        },
        hideChangeHistoryPanel: () => {
            set((state) => {
                return {...state, isVersionHistoryMode: false};
            });
        },
        documentUidChanged: () => {
            // Check if the URL indicates we should be in history mode
            // If on /history URL, preserve the current isVersionHistoryMode value (which was set by useLayoutEffect)
            // Otherwise, reset to false (the default for non-history URLs)
            // This approach fixes the flicker without setting isVersionHistoryMode too early,
            // which would prevent the change history panel from properly initializing
            const inHistoryPath =
                typeof window !== "undefined" && window.location.pathname.replace(/\/+$/, "").endsWith("/history");

            set((state) => {
                return {
                    ...state,
                    selectedCommentThreadUid: undefined,
                    // If on /history URL, preserve current value; otherwise reset to false
                    isVersionHistoryMode: inHistoryPath ? state.isVersionHistoryMode : false,
                };
            });
        },
        hideIfNotStarredByCurrentUser: false,
        setHideIfNotStarredByCurrentUser: (value: boolean) => {
            set((state) => {
                return {
                    ...state,
                    hideIfNotStarredByCurrentUser: value,
                };
            });
        },
        showAllPinLabels: false,
        setShowAllPinLabels: (show: boolean) => {
            set((state) => ({...state, showAllPinLabels: show}));
        },
        showCollaborativeCursors: true,
        setShowCollaborativeCursors: (show: boolean) => {
            set((state) => {
                return {...state, showCollaborativeCursors: show};
            });
        },

        // Comment-related stuff

        selectedCommentThreadUid: undefined,
        newCommentMessage: undefined,
        hideCommentThreads: () => {
            set((state) => {
                return {...state, selectedCommentThreadUid: undefined, newCommentMessage: undefined};
            });
        },
        showCommentThread: (commentThreadUid: string, preFillMessage?: string) => {
            set((state) => {
                if (state.selectedCommentThreadUid === commentThreadUid) {
                    return {...state};
                } else {
                    return {...state, selectedCommentThreadUid: commentThreadUid, newCommentMessage: preFillMessage};
                }
            });
        },
        toggleCommentThread: (commentThreadUid: string) => {
            set((state) => {
                if (state.selectedCommentThreadUid === commentThreadUid) {
                    return {...state, selectedCommentThreadUid: undefined};
                } else {
                    return {...state, selectedCommentThreadUid: commentThreadUid, newCommentMessage: undefined};
                }
            });
        },
        showComments: true,
        setShowComments: (show: boolean) => {
            set((state) => {
                return {...state, showComments: show};
            });
        },
        projectChatPrefillMessage: undefined,
        prefillProjectChatMessage: (text) => {
            set((state) => ({...state, projectChatPrefillMessage: text}));
        },
        removeProjectChatPrefill: () => set((state) => ({...state, projectChatPrefillMessage: undefined})),
        activeProjectDocId: undefined,
        setActiveProjectDocId: (docId) => set((state) => ({...state, activeProjectDocId: docId})),
    }));
